/**
 * Video Showcase
 * 一个主播放器 + 缩略图列表（竖向/横向 slider）
 * - 点击缩略图：把该视频切换到主播放器（不自动播放，只换封面）
 * - 点击播放按钮：插入 iframe 并开始播放
 */
class VideoShowcase extends HTMLElement {
  constructor() {
    super();

    this.player = this.querySelector("[data-showcase-player]");
    if (!this.player) return;

    this.posterImage = this.querySelector("[data-showcase-poster]");
    this.titleElement = this.querySelector("[data-showcase-title]");
    this.authorElement = this.querySelector("[data-showcase-author]");
    this.thumbs = Array.from(this.querySelectorAll("[data-showcase-thumb]"));

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener("click", () => this.selectVideo(thumb));
    });

    this.player.addEventListener("click", (event) => {
      if (event.target.closest("[data-showcase-play]")) {
        this.play();
      }
    });

    // 首屏封面也要做回退检测
    this.watchPoster();
  }

  /**
   * YouTube 在缺少 maxresdefault 时会返回 120x90 的灰色占位图（HTTP 200），
   * 因此 onerror 不会触发 —— 用 naturalWidth 判断并回退到 hqdefault。
   */
  watchPoster() {
    const image = this.posterImage;
    if (!image) return;

    // 每次都从 dataset 现读，因为切换视频时回退地址会更新
    const applyFallback = () => {
      if (image.dataset.fallbackApplied === "true") return;
      const fallback = image.dataset.posterFallback;
      if (!fallback) return;
      image.dataset.fallbackApplied = "true";
      image.src = fallback;
    };

    const verify = () => {
      if (image.naturalWidth && image.naturalWidth <= 120) applyFallback();
    };

    image.addEventListener("load", verify);
    image.addEventListener("error", applyFallback);

    if (image.complete) verify();
  }

  /**
   * 切换主播放器的视频（仅更换封面与信息，不自动播放）
   */
  selectVideo(thumb) {
    this.thumbs.forEach((item) => {
      const isActive = item === thumb;
      item.classList.toggle("is-active", isActive);
      item.setAttribute("aria-current", isActive ? "true" : "false");
    });

    // 把点击的缩略图滑到最前面（后面的视频也跟着上来）
    this.scrollThumbIntoView(thumb);

    const data = thumb.dataset;

    this.stop();

    this.player.dataset.videoId = data.videoId || "";
    this.player.dataset.videoType = data.videoType || "youtube";

    if (this.posterImage && data.poster) {
      this.posterImage.removeAttribute("srcset");
      this.posterImage.removeAttribute("sizes");

      // 换视频时重置回退状态，并带上该视频的回退地址
      this.posterImage.dataset.fallbackApplied = "false";
      this.posterImage.dataset.posterFallback = data.posterFallback || "";

      this.posterImage.src = data.poster;
      this.posterImage.alt = data.title || "";
    }

    if (this.titleElement) this.titleElement.textContent = data.title || "";
    if (this.authorElement) this.authorElement.textContent = data.author || "";

    this.player.classList.toggle(
      "has-caption",
      Boolean(data.title || data.author),
    );
  }

  /**
   * 让 slider 滑动到被点击的缩略图（复用主题 slider 的 slideByElement）
   * slider 未安装（视频数量少时）会自动返回 false，安全
   */
  scrollThumbIntoView(thumb) {
    const slide = thumb.closest(".slider-slide");
    const slider = this.querySelector("slider-component");
    if (!slide || !slider || typeof slider.slideByElement !== "function") return;
    if (!slider.initSliderStatus) return;

    const slides = slider.sliderItems || [];
    const clickedIndex = slides.indexOf(slide);
    if (clickedIndex < 0) return;

    // 点击当前置顶的那一张 -> 往回滚一张（反向）
    if (clickedIndex === slider.index && slider.index > 0) {
      slider.slideByElement(slides[slider.index - 1]);
      return;
    }

    // 其余情况：滚动到被点击的那一张（正向）
    slider.slideByElement(slide);
  }

  /**
   * 移除已插入的 iframe，回到封面状态
   */
  stop() {
    const iframe = this.player.querySelector("iframe");
    if (iframe) iframe.remove();
    this.player.classList.remove("is-playing");
  }

  /**
   * 点击播放：插入 iframe（此时才 autoplay）
   */
  play() {
    const videoId = this.player.dataset.videoId;
    if (!videoId || this.player.querySelector("iframe")) return;

    const videoType = this.player.dataset.videoType || "youtube";
    const source =
      videoType === "vimeo"
        ? `https://player.vimeo.com/video/${videoId}?autoplay=1`
        : `https://www.youtube.com/embed/${videoId}?enablejsapi=1&autoplay=1&rel=0`;

    const iframe = document.createElement("iframe");
    iframe.src = source;
    iframe.setAttribute("allow", "autoplay; encrypted-media; picture-in-picture");
    iframe.setAttribute("allowfullscreen", "");
    iframe.setAttribute("loading", "lazy");
    iframe.setAttribute(
      "title",
      this.titleElement ? this.titleElement.textContent : "Video",
    );

    this.player.appendChild(iframe);
    this.player.classList.add("is-playing");
  }
}

if (!customElements.get("video-showcase")) {
  customElements.define("video-showcase", VideoShowcase);
}
